// src/context/ThemeContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';

// Creates the context
const ThemeContext = createContext();

// Creates a custom hook for easy access to the context
export const useTheme = () => useContext(ThemeContext);

// The definitions for the colored icons
const icons = {
    light: '<path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" class="text-amber-500 dark:text-amber-400"/>',
    dark: '<path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" class="text-indigo-400 dark:text-indigo-300"/>',
    system: '<path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" class="text-sky-500 dark:text-sky-400"/>'
};

// Creates the Provider component that will wrap the app
export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

    // State to hold the SVG string for the active colored icon
    const [activeIconSvg, setActiveIconSvg] = useState('');

    useEffect(() => {
        const html = document.documentElement;
        const isDarkMode = theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
            : theme === 'dark';

        html.classList.toggle('dark', isDarkMode);
        localStorage.setItem('theme', theme);

        // Sets the active icon SVG string
        setActiveIconSvg(icons[theme]);

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemChange = () => {
            if (localStorage.getItem('theme') === 'system') {
                html.classList.toggle('dark', mediaQuery.matches);
            }
        };

        mediaQuery.addEventListener('change', handleSystemChange);
        return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }, [theme]);

    // Adds activeIconSvg to the value passed to consumers
    const value = { theme, setTheme, activeIconSvg };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};